import React, { useCallback, useMemo } from 'react';
import {
  Select,
  Table,
} from 'antd';
import { Flex } from 'antd-v5';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import OnTraccrNumberInput from '../../../../common/inputs/OnTraccrNumberInput';
import DisplayText from '../../../../common/text/DisplayText';
import CostCodeTableSelector from './CostCodeTableSelector';
import TableDeleteButton from '../TableDeleteButton';
import OnTraccrTextInput from '../../../../common/inputs/OnTraccrTextInput';
import ManualEntryDatePicker from '../../../../clock/ManualEntry/ManualEntryDatePicker';

import useCheckTableMaxRows from '../../../../common/hooks/useCheckTableMaxRows';
import { generateId, getHasColumnPermissions } from '../../../formHelpers';
import Permissions from '../../../../auth/Permissions';

import {
  currencyFormatter,
  currencyFormatter as formatter,
  currencyParser as parser,
} from '../../../../helpers/inputParsers';
import { getIdMap, isNullOrUndefined } from '../../../../helpers/helpers';
import {
  getCalculationTableColumn,
  getCustomTableColumn,
  LINE_NUMBER_COLUMN,
} from '../formFieldsHelpers';
import { getFormTableCostcodeSelectValue, getFormTableCostcodeUpdateFields } from '../../../../helpers/costcodeHelpers';

const formatCostcodeTableLabel = ({ phase, code: ccCode, name, categoryName }) => {
  const base = `${phase || 'Unphased'} - ${ccCode} ${name}`;
  return categoryName ? `${base} (${categoryName})` : base;
};

const getCostCodeColumns = ({
  t,
  requiredColumns,
  preventEdits,
  isDisplay,
  onValueUpdated,
  onProjectUpdated,
  onCostcodeUpdated,
  projectOptions,
  costcodes,
  costcodeMap,
}) => ({
  date: {
    title: <div className={requiredColumns && 'form-required-field'}>{t('Date')}</div>,
    dataIndex: 'date',
    width: 150,
    render: (date, record) => {
      if (isDisplay || preventEdits) return date;
      return (
        <ManualEntryDatePicker
          value={date}
          onChange={(newDate) => onValueUpdated(record.id, { date: newDate })}
          allowFuture
          allowClear
        />
      );
    },
  },
  description: {
    title: <div className={requiredColumns && 'form-required-field'}>{t('Description')}</div>,
    dataIndex: 'description',
    width: 200,
    render: (description, record) => {
      if (isDisplay || preventEdits) return description || '';
      return (
        <OnTraccrTextInput
          value={description}
          onChange={(e) => onValueUpdated(record.id, { description: e.target.value })}
        />
      );
    },
  },
  quantity: {
    title: <div className={requiredColumns && 'form-required-field'}>{t('Quantity')}</div>,
    dataIndex: 'quantity',
    width: 100,
    render: (quantity, record) => {
      if (isDisplay || preventEdits) return quantity ?? '';
      return (
        <OnTraccrNumberInput
          key={record.id}
          onChange={(val) => onValueUpdated(record.id, { quantity: val })}
          defaultValue={quantity}
          min={0}
          precision={2}
        />
      );
    },
  },
  rate: {
    title: <div className={requiredColumns && 'form-required-field'}>{t('Rate')}</div>,
    dataIndex: 'rate',
    width: 120,
    render: (rate, record) => {
      if (isDisplay || preventEdits) {
        if (!rate) return null;
        return currencyFormatter(parseFloat(rate));
      }
      return (
        <OnTraccrNumberInput
          key={record.id}
          formatter={formatter}
          parser={parser}
          step={0.01}
          precision={2}
          onChange={(val) => onValueUpdated(record.id, { rate: val })}
          value={rate}
        />
      );
    },
  },
  project: {
    title: <div className={requiredColumns && 'form-required-field'}>{t('Project')}</div>,
    dataIndex: 'project',
    width: 250,
    render: (project, record) => {
      if (isDisplay || preventEdits) return (project ?? '');

      return (
        <Select
          style={{ width: '100%' }}
          placeholder={`Select a ${t('Project')}`}
          showSearch
          allowClear
          optionFilterProp="label"
          options={projectOptions}
          mode="single"
          onChange={(newId) => onProjectUpdated(record.id, newId)}
          value={record?.projectId}
        />
      );
    },
  },
  costcode: {
    title: <div className={requiredColumns && 'form-required-field'}>Cost Code</div>,
    dataIndex: 'costcode',
    width: 250,
    render: (costcode, record) => {
      const {
        projectId,
        costcodeId,
        id,
      } = record || {};
      if (isDisplay || preventEdits) {
        if (!costcodeId) return '';
        return costcode;
      }

      return (
        <CostCodeTableSelector
          projectId={projectId}
          onCostcodeUpdated={onCostcodeUpdated}
          value={getFormTableCostcodeSelectValue(record)}
          entryId={id}
          costcodes={costcodes}
          costcodeMap={costcodeMap}
        />
      );
    },
  },
  amount: {
    title: <div className={requiredColumns && 'form-required-field'}>Amount</div>,
    dataIndex: 'amount',
    width: 150,
    render: (amount, record) => {
      if (isDisplay || preventEdits) {
        if (!amount) return '$0';
        return currencyFormatter(parseFloat(amount));
      }

      return (
        <OnTraccrNumberInput
          key={record.id}
          formatter={formatter}
          parser={parser}
          step={0.01}
          precision={2}
          onChange={(newAmount) => onValueUpdated(record.id, { amount: newAmount })}
          value={amount}
        />
      );
    },
  },
  hours: {
    title: <div className={requiredColumns && 'form-required-field'}>Hours</div>,
    dataIndex: 'hours',
    width: 100,
    render: (hours, record) => {
      if (isDisplay || preventEdits) {
        if (!hours) return 0;
        return hours;
      }
      return (
        <OnTraccrNumberInput
          key={record.id}
          step={1}
          precision={0}
          onChange={(val) => onValueUpdated(record.id, { hours: val })}
          value={hours}
        />
      );
    },
  },
});

const getColumns = ({
  t,
  onDelete,
  onValueUpdated,
  onProjectUpdated,
  onCostcodeUpdated,
  columns,
  isDisplay,
  requiredColumns,
  preventEdits,
  projectOptions,
  costcodes,
  costcodeMap,
  columnPermissions,
  userPositionId,
  responses,
}) => {
  const cols = [LINE_NUMBER_COLUMN];
  const colMap = getCostCodeColumns({
    t,
    isDisplay,
    onValueUpdated,
    onProjectUpdated,
    onCostcodeUpdated,
    requiredColumns,
    preventEdits,
    projectOptions,
    costcodes,
    costcodeMap,
  });

  columns.forEach((col) => {
    if (col.key in colMap) {
      cols.push(colMap[col.key]);
    } else if (col.isCustomText) {
      if (!getHasColumnPermissions({
        columnPermissions,
        column: col,
        userPositionId,
        userId: Permissions.id,
      })) {
        return;
      }
      cols.push(getCustomTableColumn(col, {
        width: 100,
        isDisplay,
        preventEdits,
        requiredColumns,
        responses,
        onChange: (record, value) => onValueUpdated(record.id, { [col.key]: value }),
      }));
    } else if (col.isCalculation) {
      cols.push(getCalculationTableColumn(col, {
        isDisplay,
      }));
    }
  });

  if (!isDisplay) {
    cols.push({
      title: '',
      dataIndex: '',
      width: 100,
      render: (_, record) => (
        <TableDeleteButton onConfirm={() => onDelete(record.id)} />
      ),
    });
  }
  return cols;
};

export default function CostCodeTablePreview({
  title = null,
  previewProps = {},
  responses = {},
  id,
  showCondensedView,
  isDisplay,
  responding,
  setResponses,
  setPreviewProps,
  columns = [],
  configProps = {},
  projectIds = [],
  costcodes = [],
  costcodeMap = {},
}) {
  const { t } = useTranslation();

  const {
    requiredColumns,
    preventEdits,
    hideAddNewButton,
    columnPermissions = {},
  } = configProps ?? {};
  const values = previewProps.values || []; // For responses
  const {
    selected: previewSelected = [],
  } = previewProps;
  const {
    [id]: {
      values: responseSelected = [],
    } = {},
  } = responses;

  const selected = responding ? responseSelected : previewSelected;

  const projects = useSelector((state) => state.projects.projects);
  const phasedCostcodes = useSelector((state) => state.costcodes.phases);
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const users = useSelector((state) => state.users.users);
  const positionNames = useSelector((state) => state.settings.positionNames);

  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const phaseMap = useMemo(() => getIdMap(phasedCostcodes), [phasedCostcodes]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const projectIdSet = useMemo(() => new Set(projectIds.filter((pId) => pId)), [projectIds]);

  const projectOptions = useMemo(() => (
    projects.filter((project) => (
      project.active && (!projectIdSet.size || projectIdSet.has(project.id))
    )).map((project) => ({ label: `${project.number} - ${project.name}`, value: project.id }))
  ), [projects, projectIdSet]);

  const updateSelected = useCallback((newSelected) => {
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [
    responding,
    responses,
    id,
    columns,
    previewProps,
    setResponses,
    setPreviewProps,
  ]);

  const onAddRowClicked = useCallback(() => {
    let projectId = null;
    let project = null;
    if (projectIds?.length === 1) {
      [projectId] = projectIds;
      project = projectMap?.[projectId]?.name;
    }
    const newEntry = {
      id: generateId(),
      projectId,
      project,
    };

    const newSelected = selected.concat([newEntry]);
    updateSelected(newSelected);
  }, [selected, updateSelected, projectIds, projectMap]);

  const onDelete = useCallback((itemId) => {
    const newSelected = selected.filter((item) => item.id !== itemId);
    updateSelected(newSelected);
  }, [updateSelected, selected]);

  const onValueUpdated = useCallback((rowId, newData = {}) => {
    const newSelected = selected.map((row) => {
      if (row.id !== rowId) return row;
      const fullRow = {
        ...row,
        ...newData,
      };
      if (!isNullOrUndefined(fullRow.rate) && !isNullOrUndefined(fullRow.quantity)) {
        const newAmount = fullRow.rate * fullRow.quantity;
        fullRow.amount = Number.isNaN(newAmount) ? 0 : parseFloat(newAmount.toFixed(2));
      }
      return fullRow;
    });
    updateSelected(newSelected);
  }, [updateSelected, selected]);

  const onProjectUpdated = useCallback((entryId, newProjectId) => {
    const ourProject = projectMap?.[newProjectId] ?? {};
    onValueUpdated(entryId, {
      projectId: ourProject.id,
      project: ourProject?.name,
      // clear selected costcode
      phaseId: null,
      phaseName: null,
      costcodeId: null,
      costcode: '',
      phaseCostcodeKey: '',
      categoryId: null,
      defaultCategoryId: null,
      customCategoryId: null,
      categoryName: null,
      description: null,
    });
  }, [projectMap, onValueUpdated]);

  const onCostcodeUpdated = useCallback((entryId, selectedVal = '') => {
    if (selectedVal == null || selectedVal === '' || typeof selectedVal !== 'string') {
      onValueUpdated(entryId, {
        ...getFormTableCostcodeUpdateFields({ selectedVal: '' }),
        description: null,
        rate: null,
      });
      return;
    }

    const costcodeFields = getFormTableCostcodeUpdateFields({
      selectedVal,
      phaseMap,
      costcodeMap,
      categoriesIdMap,
      formatCostcode: formatCostcodeTableLabel,
    });
    const costcodeObj = costcodeMap[costcodeFields.costcodeId] || {};
    const { description = '', hourlyBillingRate, dailyBillingRate } = costcodeObj;
    const rate = hourlyBillingRate ?? dailyBillingRate;

    onValueUpdated(entryId, {
      ...costcodeFields,
      description,
      rate,
    });
  }, [phaseMap, costcodeMap, categoriesIdMap, onValueUpdated]);

  const tableColumns = useMemo(() => (
    getColumns({
      t,
      onDelete,
      onValueUpdated,
      onProjectUpdated,
      onCostcodeUpdated,
      columns,
      isDisplay,
      requiredColumns,
      preventEdits,
      projectOptions,
      costcodes,
      costcodeMap,
      columnPermissions,
      userPositionId,
      responses,
    })
  ), [
    onDelete,
    onValueUpdated,
    onProjectUpdated,
    onCostcodeUpdated,
    columns,
    isDisplay,
    requiredColumns,
    preventEdits,
    projectOptions,
    costcodes,
    costcodeMap,
    columnPermissions,
    userPositionId,
    t,
    responses,
  ]);

  const dataSource = useMemo(() => (
    isDisplay && !responding ? values : selected
  ), [isDisplay, responding, values, selected]);

  const {
    shouldAddButtonBeEnabled = true,
  } = useCheckTableMaxRows({
    configProps,
    currentRowsLength: dataSource.length,
  });

  return (
    <Flex vertical style={{ marginTop: showCondensedView ? 0 : 15 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
        {title}
        {!isDisplay && !hideAddNewButton && (
          <OnTraccrButton
            title="Add Row"
            icon={<PlusOutlined />}
            onClick={onAddRowClicked}
            disabled={!shouldAddButtonBeEnabled}
          />
        )}
      </Flex>
      {!showCondensedView || dataSource?.length ? (
        <Table
          className="form-table-preview"
          style={{ width: '100%', overflow: 'auto' }}
          columns={tableColumns}
          size="small"
          pagination={false}
          dataSource={dataSource}
        />
      ) : (
        <DisplayText title="No Costcodes Selected" style={{ marginBottom: 0 }} />
      )}
    </Flex>
  );
}

/* eslint-disable react/forbid-prop-types */
CostCodeTablePreview.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.array,
  previewProps: PropTypes.object,
  setPreviewProps: PropTypes.func.isRequired,
  isDisplay: PropTypes.bool,
  id: PropTypes.string.isRequired,
  setResponses: PropTypes.func.isRequired,
  responses: PropTypes.object,
  responding: PropTypes.bool,
  configProps: PropTypes.object,
  showCondensedView: PropTypes.bool,
  projectIds: PropTypes.array,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodeMap: PropTypes.object,
};

CostCodeTablePreview.defaultProps = {
  title: null,
  columns: [],
  previewProps: {},
  responses: {},
  isDisplay: false,
  responding: false,
  configProps: {},
  showCondensedView: false,
  projectIds: [],
  costcodes: [],
  costcodeMap: {},
};
